import React, { RefObject, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import { postedMessageRetry } from '@components/Analytics/performanceAnalytics'
import { EmbeddedMessageReply } from '@components/EmbeddedMessageAttachement/EmbeddedMessage'
import { TimelineEncryptedContent } from '@components/EncryptedContent/EncryptedMessageBody'
import { FileUpload } from '@components/MediaDropContext/mediaDropTypes'
import { useUploadStore } from '@components/MediaDropContext/uploadStore'
import {
    isUrlAttachement,
    MessageAttachments,
} from '@components/MessageAttachments/MessageAttachments'
import {
    MessageLayout,
    MessageLayoutProps,
    RedactedMessageLayout,
} from '@components/MessageLayout/MessageLayout'
import { SendStatus } from '@components/MessageLayout/SendStatusIndicator'
import { useTimelineContext } from '@components/MessageTimeline/hooks/useTimelineContext'
import {
    MessageTimelineContext,
    MessageTimelineType,
} from '@components/MessageTimeline/MessageTimelineContext'
import {
    EncryptedMessageRenderEvent,
    InteractionRequestRenderEvent,
    isChannelMessage,
    isRedactedChannelMessage,
    MessageRenderEvent,
    RedactedMessageRenderEvent,
    RenderEventType,
    TokenTransferRenderEvent,
} from '@components/MessageTimeline/util/getEventsByDate'
import { MiniappTile } from '@components/Miniapp/MiniappTile'
import { ProfileHoverCard } from '@components/ProfileHoverCard/ProfileHoverCard'
import { RatioedBackgroundImage } from '@components/RatioedBackgroundImage'
import { ReplyToMessageContext } from '@components/ReplyToMessageContext/ReplyToMessageContext'
import { PastedFile } from '@components/RichTextPlate/components/PasteFilePlugin'
import {
    Attachment,
    EmbeddedMessageAttachment,
    MessageType,
    Pin,
    RiverTimelineEvent,
    ThreadStatsData,
    TimelineEvent,
} from '@towns-protocol/sdk'
import { bin_toString } from '@towns-protocol/utils'
import { Box, EditedLabel, Stack, TooltipRenderer } from '@ui'
import { AnimatePresence } from 'framer-motion'
import { useDevice } from 'hooks/useDevice'
import { useMiniappEmbed } from 'hooks/useMiniappEmbed'
import { QUERY_PARAMS } from 'routes'
import {
    parseMiniappCommand,
    staticAssertNever,
    useMessageReactions,
    useMessageThreadStats,
    useMessageTips,
    useTownsClient,
    useUserLookup,
} from 'use-towns-client'

import { InteractionRequestItem } from '../InteractionRequestItem'
import { MessageEditContextProvider } from '../MessageEditContext'
import { TimelineMessageEditor } from '../MessageEditor'
import { TokenTransfer } from '../TokenTransfer'

import { MessageBody } from './MessageBody/MessageBody'
import { QuotedMessage } from './QuotedMessage'

type ItemDataType =
    | MessageRenderEvent
    | EncryptedMessageRenderEvent
    | RedactedMessageRenderEvent
    | TokenTransferRenderEvent
    | InteractionRequestRenderEvent

type Props = {
    itemData: ItemDataType
    isHighlight?: boolean
}

export const MessageItem = (props: Props) => {
    const { itemData, isHighlight } = props
    const event = itemData.event
    const { client } = useTownsClient()

    const { isTouch } = useDevice()
    const messageTooltipRef = useRef<HTMLElement | null>(null)
    const [hoveredMentionUserId, setHoveredMentionUserId] = useState<string | undefined>(undefined)

    const timelineContext = useContext(MessageTimelineContext)

    const { canReplyInline } = useContext(ReplyToMessageContext)

    const [, setSearchParams] = useSearchParams()

    const onMediaClick = useCallback(
        (e: React.MouseEvent) => {
            if (!event.threadParentId || event.threadParentId.length === 0) {
                setSearchParams((params) => ({
                    ...params,
                    [QUERY_PARAMS.GALLERY_ID]: event.eventId,
                }))
                return
            }
            setSearchParams((params) => ({
                ...params,
                [QUERY_PARAMS.GALLERY_ID]: event.eventId,
                [QUERY_PARAMS.GALLERY_THREAD_ID]: event.threadParentId,
            }))
        },
        [event.threadParentId, event.eventId, setSearchParams],
    )

    const onRetryClick = useCallback(() => {
        if (!event.localEventId || !timelineContext?.channelId || !client) {
            return
        }
        postedMessageRetry()
        client.retrySendMessage(timelineContext.channelId, event.localEventId)
    }, [event.localEventId, timelineContext?.channelId, client])

    const messageUploads = useUploadStore((state) =>
        event.localEventId ? state.uploads[event.localEventId]?.uploads : undefined,
    )

    const attachedLinks = useMemo(() => {
        return (
            (event.content.kind === RiverTimelineEvent.ChannelMessage &&
                event.content.attachments?.filter(isUrlAttachement).map((a) => a.url)) ||
            []
        )
    }, [event.content])

    const threadStats = useMessageThreadStats(timelineContext?.channelId, event.eventId)

    // assuming an embedded message from the same channel is a reply to
    const embeddedMessageReply = useMemo(() => {
        if (event.content.kind !== RiverTimelineEvent.ChannelMessage) {
            return undefined
        }
        return event.content.attachments?.find(
            (a) =>
                a.type === 'embedded_message' &&
                a.info.channelId === timelineContext?.channelId &&
                !a.channelMessageEvent?.threadId,
        ) as EmbeddedMessageAttachment | undefined
    }, [timelineContext?.channelId, event.content])

    // the inline reply parent is represented as an attachment, so we need to remove it from the attachments list
    const attachmentsWithoutReply = useMemo(() => {
        if (event.content.kind !== RiverTimelineEvent.ChannelMessage) {
            return undefined
        }
        return (
            event.content.attachments?.filter((a) => a.id !== embeddedMessageReply?.id) || undefined
        )
    }, [event.content, embeddedMessageReply?.id])

    if (!timelineContext) {
        return <></>
    }

    const { channels, channelId, spaceId, onMentionClick, timelineActions } = timelineContext

    const isMessage = itemData.type === RenderEventType.Message
    const isTokenTransfer = itemData.type === RenderEventType.TokenTransfer
    const isEncryptedMessage = itemData.type === RenderEventType.EncryptedMessage
    const isInteractionRequest = itemData.type === RenderEventType.InteractionRequest
    const isRedacted = isRedactedChannelMessage(event)

    const displayContext =
        isMessage || isEncryptedMessage || isTokenTransfer || isInteractionRequest
            ? itemData.displayContext
            : 'single'
    const isEditing = event.eventId === timelineActions.editingMessageId

    const pin = timelineContext.pins?.find((p) => p.event.hashStr === event.eventId)

    const msgTypeKey = getItemContentKey(itemData)

    // replies are only shown for channel messages (two levels only)
    const replies = timelineContext.type === MessageTimelineType.Channel ? threadStats : undefined

    const isThreadParent = !!threadStats

    if (isRedacted && (replies || isThreadParent)) {
        // display specific layout for redacted threads
        return <RedactedMessageLayout replies={replies} event={event} />
    } else if (isRedacted) {
        // deleted messages that are not threads are removed before getting here
        // this is merely as safety check + to make TS happy
        return null
    }

    return (
        <MessageWrapper
            pin={pin}
            highlight={isHighlight}
            event={event}
            selectable={!isEditing && !isEncryptedMessage}
            isEncryptedMessage={isEncryptedMessage}
            displayContext={displayContext}
            replies={replies}
            key={`${event.eventId}${event.updatedAtEpochMs ?? event.createdAtEpochMs}${msgTypeKey}`}
            attachments={attachmentsWithoutReply}
            onMediaClick={onMediaClick}
        >
            {canReplyInline ? (
                embeddedMessageReply ? (
                    <EmbeddedMessageReply attachment={embeddedMessageReply} />
                ) : event.replyParentId ? (
                    <QuotedMessage eventId={event.replyParentId} />
                ) : null
            ) : null}

            {isEncryptedMessage ? (
                <TimelineEncryptedContent event={event} content={itemData.event.content} />
            ) : isInteractionRequest ? (
                <InteractionRequestItem event={event} request={itemData.event.content} />
            ) : event.content.kind === RiverTimelineEvent.ChannelMessage ? (
                isEditing ? (
                    <>
                        <TimelineMessageEditor
                            initialValue={event.content.body}
                            eventId={event.eventId}
                            latestEventId={event.latestEventId}
                            eventContent={event.content}
                            channelId={channelId}
                            spaceId={spaceId}
                            attachments={event.content.attachments}
                        />

                        {/* Always show message on touch devices, even while editing also disables onMentionClick. */}
                        {isTouch && (
                            <MessageBody
                                attachedLinks={attachedLinks}
                                eventContent={event.content}
                                event={event}
                                channels={channels}
                            />
                        )}
                    </>
                ) : event.content.content.msgType === MessageType.Image ? (
                    <RatioedBackgroundImage
                        url={event.content.content.info?.url ?? ''}
                        width={event.content.content.thumbnail?.width}
                        height={event.content.content.thumbnail?.height}
                        onClick={onMediaClick}
                    />
                ) : (
                    <>
                        <MessageBody
                            attachedLinks={attachedLinks}
                            eventContent={event.content}
                            event={event}
                            channels={channels}
                            onMentionClick={onMentionClick}
                            onMentionHover={(ref, userId) => {
                                messageTooltipRef.current = ref ?? null
                                setHoveredMentionUserId(userId)
                            }}
                            onRetrySend={onRetryClick}
                        />
                        {hoveredMentionUserId && messageTooltipRef.current && !isTouch && (
                            <TooltipRenderer
                                active
                                alignRef={messageTooltipRef as RefObject<HTMLElement>}
                                tooltip={<ProfileHoverCard userId={hoveredMentionUserId} />}
                            />
                        )}
                        {!!messageUploads?.length && <MessageUploads uploads={messageUploads} />}
                    </>
                )
            ) : event.content.kind === RiverTimelineEvent.TokenTransfer ? (
                <TokenTransfer event={event.content} />
            ) : (
                <>not a message</>
            )}
        </MessageWrapper>
    )
}

const MessageUploads = (props: { uploads: FileUpload[] }) => {
    const { uploads } = props

    return (
        <Box gap horizontal>
            <AnimatePresence>
                {uploads.map((u) => (
                    <PastedFile
                        sending={u.progress < 1}
                        id={u.id}
                        key={u.id}
                        content={u.content}
                        progress={u.progress}
                        failed={u.failed}
                        waitingToSend={false}
                    />
                ))}
            </AnimatePresence>
        </Box>
    )
}

type MessageWrapperProps = {
    displayContext?: MessageLayoutProps['displayContext']
    event: TimelineEvent
    highlight?: boolean
    selectable?: boolean
    isEncryptedMessage?: boolean
    children: React.ReactNode
    replies?: ThreadStatsData
    onMediaClick: (e: React.MouseEvent) => void
    pin?: Pin
    attachments?: Attachment[]
}

const MessageWrapper = React.memo((props: MessageWrapperProps) => {
    const {
        event,
        displayContext,
        selectable,
        replies,
        onMediaClick,
        isEncryptedMessage,
        attachments,
    } = props
    const { sender } = event
    const timelineContext = useTimelineContext()
    const { isTouch } = useDevice()
    const user = useUserLookup(sender.id)

    const isChannelMessageEvent = isChannelMessage(event)
    const messageBodyValue = isChannelMessageEvent ? event.content.body : undefined
    const miniappCommand = messageBodyValue ? parseMiniappCommand(messageBodyValue) : null

    const {
        embed: miniappEmbed,
        isLoading: miniappIsLoading,
        isFetching: miniappIsFetching,
    } = useMiniappEmbed(miniappCommand?.url)
    const isMiniappLoading = miniappIsLoading || miniappIsFetching

    const {
        channelId,
        handleReaction,
        isChannelWritable,
        isChannelPinnable,
        isChannelReactable,
        pins,
        spaceId,
        threadParentId,
        type,
        userId,
    } = timelineContext

    const messageTips = useMessageTips(channelId, event.eventId)
    const isOwn = event.content?.kind == RiverTimelineEvent.ChannelMessage && sender.id === userId

    const reactions = useMessageReactions(channelId, event.eventId)
    const isEditing = event.eventId === timelineContext.timelineActions.editingMessageId

    const sendStatus: undefined | SendStatus = useMemo(
        () =>
            event.localEventId
                ? {
                      isLocalPending: event.isLocalPending,
                      isEncrypting: event.isEncrypting,
                      isFailed: event.isSendFailed,
                  }
                : undefined,
        [event.isEncrypting, event.isLocalPending, event.isSendFailed, event.localEventId],
    )

    const [, setSearchParams] = useSearchParams()
    const hasThreadParent = !!threadParentId && threadParentId.length > 0
    const onAttachmentClick = useCallback(
        (attachmentId: string) => {
            if (!hasThreadParent) {
                setSearchParams({
                    [QUERY_PARAMS.GALLERY_ID]: attachmentId,
                })
                return
            }
            return setSearchParams({
                [QUERY_PARAMS.GALLERY_ID]: attachmentId,
                [QUERY_PARAMS.GALLERY_THREAD_ID]: threadParentId,
            })
        },
        [setSearchParams, hasThreadParent, threadParentId],
    )

    const pin = pins?.find(
        (p) => p.event.hashStr === event.eventId || p.event.hashStr === event.latestEventId,
    )

    const messageLayout = !event ? null : (
        <MessageLayout
            avatarSize="avatar_x4"
            editing={isEditing}
            id={`event-${event.localEventId ?? event.eventId}`}
            highlight={props.highlight}
            selectable={selectable}
            isEncryptedMessage={isEncryptedMessage}
            userId={userId}
            senderId={sender.id}
            canPin={!event.isLocalPending && isChannelPinnable}
            canReply={
                isChannelWritable && !event.isLocalPending && type !== MessageTimelineType.Thread
            }
            timestamp={event.createdAtEpochMs}
            channelId={channelId}
            editable={isOwn && !event.isLocalPending}
            eventId={event.eventId}
            latestEventId={event.latestEventId}
            threadParentId={event.threadParentId}
            displayContext={displayContext}
            isChannelWritable={isChannelWritable}
            isChannelReactable={isChannelReactable}
            user={user}
            paddingTop={displayContext === 'head' || displayContext === 'single' ? 'md' : 'sm'}
            paddingBottom={
                displayContext === 'tail' || displayContext === 'single'
                    ? 'md'
                    : event.content?.kind === RiverTimelineEvent.TokenTransfer
                      ? '2'
                      : 'sm'
            }
            paddingX="md"
            spaceId={spaceId}
            reactions={reactions}
            tips={messageTips}
            replies={replies}
            relativeDate={hasThreadParent}
            messageBody={messageBodyValue}
            sendStatus={sendStatus}
            sessionId={event.sessionId}
            pin={pin}
            onReaction={handleReaction}
        >
            {/* default: show message above attachments */}
            {!isEditing && props.children}

            {miniappCommand && (
                <Box paddingTop="xs">
                    <Box style={{ maxWidth: '480px', width: '100%' }}>
                        <MiniappTile
                            embed={miniappEmbed}
                            miniappUrl={miniappCommand.url}
                            eventId={event.eventId}
                            isLoading={isMiniappLoading}
                        />
                    </Box>
                </Box>
            )}

            {attachments && attachments.length > 0 && (
                <Stack gap="sm">
                    <MessageAttachments
                        eventId={event.eventId}
                        attachments={attachments}
                        threadParentId={threadParentId}
                        createdAt={event.confirmedAtEpochMs} // use confirmed at so that clients can't cheat
                        onAttachmentClick={onAttachmentClick}
                        onClick={isTouch && selectable ? onMediaClick : undefined}
                    />
                </Stack>
            )}

            {/* Show edited label below attachments when criteria met */}
            {!isEditing &&
                event.content?.kind === RiverTimelineEvent.ChannelMessage &&
                event.content.editsEventId !== undefined &&
                shouldShowEditedLabelBelowAttachments(event) && (
                    <Box paddingTop="xs">
                        <EditedLabel />
                    </Box>
                )}

            {/*  when editing: show attachemts above message */}
            {isEditing && props.children}
        </MessageLayout>
    )

    if (isEditing) {
        return <MessageEditContextProvider>{messageLayout}</MessageEditContextProvider>
    } else {
        return messageLayout
    }
})

function getItemContentKey(itemData: ItemDataType): string {
    switch (itemData.type) {
        case RenderEventType.Message:
            return itemData.event.content.content.msgType ?? ''
        case RenderEventType.EncryptedMessage:
            return itemData.event.content.error === undefined ? 'enc' : 'enc-error'
        case RenderEventType.RedactedMessage:
            return 'redacted'
        case RenderEventType.TokenTransfer: {
            const transaction = itemData?.event.content.transaction
            if (!transaction) {
                return ''
            }
            return transaction.receipt?.transactionHash
                ? bin_toString(transaction.receipt.transactionHash)
                : (transaction.solanaReceipt?.transaction?.signatures[0] ?? '')
        }
        case RenderEventType.InteractionRequest:
            return 'interaction-request'
        default:
            staticAssertNever(itemData)
    }
}

function shouldShowEditedLabelBelowAttachments(event: TimelineEvent): boolean {
    if (event.content?.kind !== RiverTimelineEvent.ChannelMessage) {
        return false
    }

    const hasAttachments = Boolean(
        event.content.attachments && event.content.attachments.length > 0,
    )
    const hasBody = Boolean(event.content.body && event.content.body.trim().length > 0)

    return hasAttachments && !hasBody
}
